import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { SectionCard } from './SectionCard'
import { VIDEO_ORDER, VIDEO_LABELS, normalizeVideoMetrics } from './videoMetrics'
import type { PerformanceRow } from '../../../types/dashboard'

const ROW_COLORS = [
  'bg-brand-teal', 'bg-brand-cyan', 'bg-primary-600', 'bg-brand-turquoise',
  'bg-brand-pearl', 'bg-primary-700', 'bg-primary-800', 'bg-primary-900',
]

interface StatProps {
  label: string
  value: string
}
function Stat({ label, value }: StatProps) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-base font-bold text-gray-900 dark:text-white">{value}</span>
      <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</span>
    </div>
  )
}

interface Props {
  rows: PerformanceRow[]
  campaignId: number
  sectionRef?: React.RefObject<HTMLDivElement>
}

export function AdUnitsSection({ rows, campaignId, sectionRef }: Props) {
  const navigate = useNavigate()

  if (rows.length === 0) return null

  return (
    <SectionCard title="Ad Units" sectionRef={sectionRef} id="section-ad-units">
      <div className="flex flex-col gap-3">
        {rows.map((row, i) => {
          const norm = normalizeVideoMetrics(row.video_metrics)
          const videoEntries = VIDEO_ORDER.filter(k => norm[k] !== undefined && norm[k].count > 0)
          const color = ROW_COLORS[i % ROW_COLORS.length]

          return (
            <div
              key={row.id ?? i}
              className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              {/* Header row */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`shrink-0 w-3 h-3 rounded-full ${color}`} />
                  <span className="font-semibold text-gray-900 dark:text-white truncate">{row.name}</span>
                  {row.type && (
                    <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      {row.type}
                    </span>
                  )}
                  {row.sizes && row.sizes.length > 0 && (
                    <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">
                      {row.sizes.join(', ')}
                    </span>
                  )}
                </div>
                {row.id != null && (
                  <button
                    onClick={() => navigate(`/dashboard/campaign/${campaignId}/ad_unit/${row.id}`)}
                    className="shrink-0 flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium ml-4"
                  >
                    View Report <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Metrics rows */}
              <div className="px-4 py-3 flex flex-col gap-3">
                <div className="flex items-start gap-x-8">
                  <Stat label="Impressions" value={row.impressions.toLocaleString()} />
                  <Stat label="Clicks"      value={row.clicks.toLocaleString()} />
                  <Stat label="CTR %"       value={`${row.ctr.toFixed(2)}%`} />
                  <Stat label="Engagement"  value={row.engagement.toLocaleString()} />
                  <Stat label="Eng. Rate %" value={`${row.engagement_rate.toFixed(2)}%`} />
                </div>

                {videoEntries.length > 0 && (
                  <div className="flex items-start gap-x-8 pt-2 border-t border-gray-100 dark:border-gray-700/50">
                    {videoEntries.map(k => {
                      const m = norm[k]
                      return (
                        <Stat
                          key={k}
                          label={VIDEO_LABELS[k] || k}
                          value={`${m.count.toLocaleString()} (${m.pct.toFixed(2)}%)`}
                        />
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </SectionCard>
  )
}
